import React, { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface FileUploadProps {
  name: string;
  label?: string;
  id?: string;
  disabled?: boolean;
  required?: boolean;
  error?: boolean;
  errorMessage?: string;
  className?: string;
  accept?: string;
  value?: File | string | null;
  helperText?: string;
  onBlur?: (_: any, name: string) => void;
  onChange?: (_: any, name: string) => void;
  onRemove?: () => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  label,
  id,
  disabled,
  name,
  required,
  error,
  errorMessage,
  className,
  accept = '.pdf',
  value,
  helperText,
  onBlur,
  onChange,
  onRemove,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [localError, setLocalError] = useState<string | null>(null);

  const getFileName = () => {
    if (value instanceof File) {
      return value.name;
    }
    if (typeof value === 'string') {
      const parts = value.split('/');
      return parts[parts.length - 1];
    }
    return null;
  };

  const getFileUrl = () => {
      if (value instanceof File) {
          return URL.createObjectURL(value);
      }
      if (typeof value === 'string') {
          return value;
      }
      return undefined;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      // Check file size limit (Example: 5 MB)
      const _fileSizeInMB = file.size / (1024 * 1024);
      // if (_fileSizeInMB > 5) {
      //   setLocalError(t('common.fileUpload.fileSizeError') || 'File size exceeds 5 MB');
      //   e.target.value = ''; // Reset input
      //   return;
      // }

      setLocalError(null);

      if (onChange) {
        const syntheticEvent = {
          target: {
            value: file,
            name: name,
            files: e.target.files,
          },
        };
        onChange(syntheticEvent, name);
      }
    }
    // Reset input value so same file can be selected again
    e.target.value = '';
  };

  const handleReset = () => {
    // Always clear local input first
    if (fileInputRef.current) {
        fileInputRef.current.value = '';
    }
    setLocalError(null);

    // Call parent handler if it exists
    if (onRemove) {
      onRemove();
    } else if (onChange) {
      // Only trigger null change event if onRemove is not provided
      const syntheticEvent = {
        target: {
          value: null,
          name: name,
          files: null,
        },
      };
      onChange(syntheticEvent, name);
    }
  };

  const handleUploadClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  return (
    <div className={`file-upload-wrapper ${className || ''}`}>
      <div className="d-flex flex-column flex-sm-row align-items-center gap-4">
        {/* File Preview / Icon */}
        <div className="file-upload-preview">
            <div 
                className="rounded d-flex align-items-center justify-content-center bg-light" 
                style={{ 
                    width: '100px', 
                    height: '100px', 
                    border: '2px solid var(--bs-border-color)' 
                }}
            >
            {value ? (
                    <a 
                    href={getFileUrl()} 
                    target="_blank" 
                    rel="noreferrer" 
                    title={getFileName() || 'View File'}
                    className="d-flex align-items-center justify-content-center w-100 h-100 text-decoration-none"
                    >
                    <i className="ri-file-pdf-2-fill text-danger" style={{ fontSize: '3rem' }}></i>
                    </a>
            ) : (
                    <span className="text-muted small text-center p-2" style={{ fontSize: '0.75rem', lineHeight: '1.2' }}>
                        {t('common.messages.noFileSelected')}
                    </span>
            )}
            </div>
        </div>

        {/* Upload Controls */}
        <div className="d-flex flex-column gap-3 flex-grow-1">
          <div className="d-flex flex-column flex-sm-row gap-3">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleUploadClick}
              disabled={disabled}
            >
              <i className="ri-upload-2-line me-2"></i>
              {t('common.buttons.upload') || 'Upload'}
            </button>
            {value && (
              <a
                href={getFileUrl()}
                target="_blank"
                rel="noreferrer"
                className="btn btn-outline-secondary"
                title={t('common.buttons.download') || 'Download'}
                download
              >
                <i className="ri-download-2-line"></i>
              </a>
            )}
            <button
              type="button"
              className="btn btn-outline-danger"
              onClick={handleReset}
              disabled={disabled || !value}
            >
              <i className="ri-delete-bin-line"></i>
            </button>
          </div>

          <div className="d-flex flex-column">
             {/* Helper Text */}
            <small className="text-muted">
                {helperText || t('common.fileUpload.helperText') || 'Allowed PDF. Max 5MB.'}
            </small>

             {/* Display Name if selected */}
             {getFileName() && (
                 <small className="fw-medium text-truncate text-dark mt-1" style={{ maxWidth: '250px' }}>
                     {getFileName()}
                 </small>
             )}
          </div>

          {/* Hidden File Input */}
          <input
            ref={fileInputRef}
            id={id}
            name={name}
            type="file"
            accept={accept}
            disabled={disabled}
            className="d-none"
            onChange={handleFileChange}
            onBlur={(e) => onBlur && onBlur(e, name)}
          />

          {/* Label */}
          {label && (
            <small className="fw-medium">
              {label} {required && <span className="text-danger">*</span>}
            </small>
          )}

          {/* Error Message */}
          {(error || localError) && (
            <div className="fv-plugins-message-container fv-plugins-message-container--enabled invalid-feedback d-block">
              <div data-field={id} data-validator="notEmpty">
                {localError || errorMessage}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUpload;
